import React from 'react';
import { CalendarRange } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  applyPresetWithComparison,
  detectActivePreset,
  formatPeriodLabel,
  type DiretoriaPeriodPreset,
} from '@/lib/diretoriaPeriod';

export interface DiretoriaPeriodBarProps {
  dataFrom: string;
  dataTo: string;
  compareFrom: string;
  compareTo: string;
  onChange: (primaryFrom: string, primaryTo: string, compareFrom: string, compareTo: string) => void;
  className?: string;
}

export const DiretoriaPeriodBar: React.FC<DiretoriaPeriodBarProps> = ({
  dataFrom,
  dataTo,
  compareFrom,
  compareTo,
  onChange,
  className,
}) => {
  const activePreset = detectActivePreset(dataFrom, dataTo);

  const applyPreset = (preset: DiretoriaPeriodPreset) => {
    const { primary, comparison } = applyPresetWithComparison(preset);
    onChange(primary.dataFrom, primary.dataTo, comparison.dataFrom, comparison.dataTo);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex rounded-md border border-border p-0.5">
          {(
            [
              ['hoje', 'Hoje'],
              ['semana', 'Semana'],
              ['mes', 'Mês'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => applyPreset(key)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                activePreset === key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-border/80 bg-muted/20 px-2 py-1.5">
          <span className="w-full text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:w-auto sm:pb-2">
            Período
          </span>
          <label className="flex flex-col gap-0.5 text-xs text-muted-foreground">
            De
            <input
              type="date"
              value={dataFrom}
              max={dataTo}
              onChange={(e) => onChange(e.target.value, dataTo, compareFrom, compareTo)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            />
          </label>
          <label className="flex flex-col gap-0.5 text-xs text-muted-foreground">
            Até
            <input
              type="date"
              value={dataTo}
              min={dataFrom}
              onChange={(e) => onChange(dataFrom, e.target.value, compareFrom, compareTo)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            />
          </label>
        </div>
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-border/80 bg-muted/20 px-2 py-1.5">
          <span className="w-full text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:w-auto sm:pb-2">
            Comparar com
          </span>
          <label className="flex flex-col gap-0.5 text-xs text-muted-foreground">
            De
            <input
              type="date"
              value={compareFrom}
              max={compareTo}
              onChange={(e) => onChange(dataFrom, dataTo, e.target.value, compareTo)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            />
          </label>
          <label className="flex flex-col gap-0.5 text-xs text-muted-foreground">
            Até
            <input
              type="date"
              value={compareTo}
              min={compareFrom}
              onChange={(e) => onChange(dataFrom, dataTo, compareFrom, e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            />
          </label>
        </div>
      </div>
      <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
        <CalendarRange className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Período: <span className="font-medium text-foreground">{formatPeriodLabel(dataFrom, dataTo)}</span>
          {' · '}
          Comparando com{' '}
          <span className="font-medium text-foreground">
            {formatPeriodLabel(compareFrom, compareTo)}
          </span>
        </span>
      </p>
    </div>
  );
};
